import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { coin } from "src/assets";
import { s3baseUrl } from "src/config/config";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrAvatar from "src/design-system/components/GrAvatar";
import GrBadge from "src/design-system/components/GrBadge";
import { grCleanName } from "src/design-system/components/GrChatListRow";

// People in the likes / shared coins popups, Facebook-style: photo with a small reaction mark on its
// corner (a red heart for likes, the coin for coins), name and the Admin / Coach badge (as on posts),
// one row per person with a hairline between rows. `coins`: each item is a coins activity
// ({ sender, points_earned, description }), shown with its "Shared Coins:" and "Comment:" lines.
// The list scrolls inside the popup (never taller than the window allows) so the title and × stay put.

// Width of the likes / shared coins popups (GrDialog maxWidth): a narrow list of names.
export const PEOPLE_DIALOG_WIDTH = 520;

// A hairline under the title, then the people; the list keeps a comfortable height even with one or two
// people, so the popup never looks like a small strip.
const listSx = {
  listStyle: "none",
  m: 0,
  mt: gr.space[3],
  p: 0,
  borderTop: `1px solid ${gr.color.line}`,
  minHeight: `calc(${gr.space[12]} * 5)`,
  // Window height minus the dialog's margins, padding and title row (so the dialog never scrolls
  // as a whole), capped at 480px on tall screens.
  maxHeight: `min(calc(${gr.space[12]} * 10), calc(100vh - ${gr.space[12]} * 4))`,
  overflowY: "auto",
  overscrollBehavior: "contain",
};
const rowSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: gr.space[4],
  py: gr.space[4],
  "& + &": { borderTop: `1px solid ${gr.color.line2}` },
};
const avatarWrapSx = { position: "relative", flex: "none" };
// The reaction mark: a round chip on the avatar's bottom-right corner, ringed in white so it
// stays readable over any photo.
const markSx = {
  position: "absolute",
  right: `calc(${gr.space[1]} * -1)`,
  bottom: `calc(${gr.space[1]} * -1)`,
  width: gr.icon.lg,
  height: gr.icon.lg,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  borderRadius: gr.radius.pill,
  border: `2px solid ${gr.color.surface}`,
  overflow: "hidden",
};
const heartMarkSx = {
  ...markSx,
  background: gr.color.danger,
  color: gr.color.onBrand,
};
const coinMarkSx = { ...markSx, background: gr.color.surface };
const markIconStyle = { width: gr.space[3], height: gr.space[3] };
const coinImageSx = { width: "100%", height: "100%", objectFit: "contain" };
const bodySx = { minWidth: 0, flex: 1, alignSelf: "center" };
const nameRowSx = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: gr.space[2],
};
const nameSx = {
  fontSize: gr.font.size.lg,
  fontWeight: gr.font.weight.medium,
  color: gr.color.ink,
  overflowWrap: "anywhere",
};
const detailSx = {
  mt: gr.space[1],
  fontSize: gr.font.size.sm,
  color: gr.color.ink2,
  overflowWrap: "anywhere",
};
const detailLabelSx = {
  fontWeight: gr.font.weight.semibold,
  color: gr.color.brandStrong,
  mr: gr.space[1],
};

function ReactionMark({ coins }) {
  return coins ? (
    <Box component="span" sx={coinMarkSx}>
      <Box component="img" src={coin} alt="" sx={coinImageSx} />
    </Box>
  ) : (
    <Box component="span" sx={heartMarkSx}>
      <GrIcon icon={icons.heartFilled} style={markIconStyle} />
    </Box>
  );
}

ReactionMark.propTypes = { coins: PropTypes.bool };

export default function UsersList({ feedActivity = [], coins = false }) {
  return (
    <Box component="ul" sx={listSx}>
      {feedActivity.map((activity, index) => {
        const person = (coins ? activity?.sender : activity) || {};
        const name = grCleanName(person.name);
        return (
          <Box component="li" key={activity?._id || index} sx={rowSx}>
            <Box sx={avatarWrapSx}>
              <GrAvatar src={s3baseUrl + person.profile_image} name={name} />
              <ReactionMark coins={coins} />
            </Box>
            <Box sx={bodySx}>
              <Box sx={nameRowSx}>
                <Box component="span" sx={nameSx}>
                  {name}
                </Box>
                {person.user_type !== "member_user" ? (
                  <GrBadge tone="brand">
                    {person.user_type === "admin_user" ? "Admin" : "Coach"}
                  </GrBadge>
                ) : null}
              </Box>
              {coins ? (
                <>
                  <Box sx={detailSx}>
                    <Box component="span" sx={detailLabelSx}>
                      Shared Coins:
                    </Box>
                    {activity?.points_earned}
                  </Box>
                  <Box sx={detailSx}>
                    <Box component="span" sx={detailLabelSx}>
                      Comment:
                    </Box>
                    {activity?.description}
                  </Box>
                </>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

UsersList.propTypes = {
  feedActivity: PropTypes.array,
  coins: PropTypes.bool,
};
